import { useState } from "react";
import { 
    Mail,
    User,
    Globe,
    Lock,
    ArrowRight,
    Loader2,
    Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadCaptureGate({ 
    score, 
    maxScore, 
    toolName,
    previewContent,
    onUnlock 
}) {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        website: ""
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = "Please enter your name";
        }
        
        if (!formData.email.trim()) {
            newErrors.email = "Please enter your email";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email";
        }
        
        if (!formData.website.trim()) {
            newErrors.website = "Please enter your website URL";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setIsSubmitting(true);
        
        try {
            // Store lead data
            await axios.post(`${API}/leads/tool-submission`, {
                name: formData.name,
                email: formData.email,
                website: formData.website,
                tool: toolName,
                score: score,
                max_score: maxScore
            });
            
            toast.success("Results unlocked!");
            onUnlock(formData);
        } catch (error) {
            console.error("Failed to submit:", error);
            // Still unlock even if API fails - don't block user
            toast.success("Results unlocked!");
            onUnlock(formData);
        } finally {
            setIsSubmitting(false);
        }
    };

    const scorePercent = Math.round((score / maxScore) * 100);

    return (
        <div className="animate-fade-in" data-testid="lead-capture-gate">
            {/* Preview Score */}
            <Card className="bg-[#111111] border-white/10 mb-6">
                <CardContent className="p-8 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm mb-6">
                        <Sparkles className="h-4 w-4" />
                        Your Results Are Ready
                    </div>
                    
                    {/* Score Preview */}
                    <div className="relative w-32 h-32 mx-auto mb-6">
                        <svg className="w-full h-full transform -rotate-90">
                            <circle
                                cx="64"
                                cy="64"
                                r="56"
                                stroke="rgba(255,255,255,0.1)"
                                strokeWidth="8"
                                fill="none"
                            />
                            <circle
                                cx="64"
                                cy="64"
                                r="56"
                                stroke="url(#previewGradient)"
                                strokeWidth="8"
                                fill="none"
                                strokeLinecap="round"
                                strokeDasharray={`${(score / maxScore) * 352} 352`}
                            />
                            <defs>
                                <linearGradient id="previewGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#06b6d4" />
                                    <stop offset="100%" stopColor="#8b5cf6" />
                                </linearGradient>
                            </defs>
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-4xl font-bold text-white">{score}</span>
                            <span className="text-gray-500 text-xs">out of {maxScore}</span>
                        </div>
                    </div>

                    {/* Preview Content */}
                    {previewContent}
                </CardContent>
            </Card>

            {/* Locked Content Indicator */}
            <Card className="bg-gradient-to-b from-[#111111] to-[#0a0a0a] border-white/10 mb-6 overflow-hidden">
                <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                            <Lock className="h-5 w-5 text-violet-400" />
                        </div>
                        <div>
                            <h3 className="text-white font-semibold">Unlock Full Results</h3>
                            <p className="text-sm text-gray-500">Enter your details to see the detailed breakdown</p>
                        </div>
                    </div>

                    {/* Blurred preview of what's locked */}
                    <div className="relative mb-6">
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/80 to-[#0a0a0a] z-10" />
                        <div className="blur-sm opacity-50 pointer-events-none">
                            <div className="space-y-2">
                                <div className="h-4 bg-white/10 rounded w-full" />
                                <div className="h-4 bg-white/10 rounded w-3/4" />
                                <div className="h-4 bg-white/10 rounded w-5/6" />
                                <div className="h-4 bg-white/10 rounded w-2/3" />
                            </div>
                        </div>
                    </div>

                    {/* Lead Capture Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                <Input
                                    type="text"
                                    placeholder="Your name"
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    className={`pl-10 bg-black/50 border-gray-800 text-white h-11 ${errors.name ? 'border-red-500' : ''}`}
                                    data-testid="lead-name-input"
                                />
                            </div>
                            {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                        </div>

                        <div>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                <Input
                                    type="email"
                                    placeholder="your@email.com"
                                    value={formData.email}
                                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                    className={`pl-10 bg-black/50 border-gray-800 text-white h-11 ${errors.email ? 'border-red-500' : ''}`}
                                    data-testid="lead-email-input"
                                />
                            </div>
                            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                        </div>

                        <div>
                            <div className="relative">
                                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                <Input
                                    type="text"
                                    placeholder="yourwebsite.com"
                                    value={formData.website}
                                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                                    className={`pl-10 bg-black/50 border-gray-800 text-white h-11 ${errors.website ? 'border-red-500' : ''}`}
                                    data-testid="lead-website-input"
                                />
                            </div>
                            {errors.website && <p className="text-red-400 text-xs mt-1">{errors.website}</p>}
                        </div>

                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold py-5"
                            data-testid="unlock-results-btn"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Unlocking...
                                </>
                            ) : (
                                <>
                                    Unlock My Full Results
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </>
                            )}
                        </Button>

                        <p className="text-xs text-gray-500 text-center">
                            We respect your privacy. No spam, ever.
                        </p>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
